import { useContext, useEffect } from 'react';
import { AppContext } from '../../AppContext';
import { GetSearchTaskEvent, GetSearchTaskResult } from 'bloggers-backend/lib/youtube/domain/events';
import { SearchTask } from 'bloggers-backend/lib/youtube/domain/SearchTask';
import { hasSearchTaskChanged } from './searchTaskPolling';

const POLL_INTERVAL_MS = 2000;

interface UsePollingSearchTaskOptions {
  id: string | undefined;
  status: string;
  currentValues: SearchTask;
  onSearchTaskUpdated: (task: SearchTask) => void;
}

export function usePollingSearchTask({id, status, currentValues, onSearchTaskUpdated}: UsePollingSearchTaskOptions) {
  const {client, notificationsService} = useContext(AppContext);

  useEffect(() => {
    if (!id || status !== 'in progress') {
      return;
    }

    const taskId = id;
    let cancelled = false;
    let lastPolledValues = currentValues;
    let timeoutId: ReturnType<typeof setTimeout>;

    async function poll() {
      try {
        const result = await client<GetSearchTaskEvent, GetSearchTaskResult>(
          {event: 'getSearchTask', data: {id: taskId}}
        )

        if (cancelled) {
          return;
        }

        if (result.success) {
          if (hasSearchTaskChanged(lastPolledValues, result.value)) {
            lastPolledValues = result.value;
            onSearchTaskUpdated(result.value)
          }
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS)
        } else {
          notificationsService.notify({
            message: result.errors ? JSON.stringify(result.errors) : 'Failed to refresh the search task',
            severity: "error",
          })
        }
      } catch {
        if (!cancelled) {
          notificationsService.notify({
            message: 'Failed to refresh the search task',
            severity: "error",
          })
        }
      }
    }

    timeoutId = setTimeout(poll, POLL_INTERVAL_MS)

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- client/notificationsService come from context and are stable; onSearchTaskUpdated is a fresh function each render but is captured once and only ever forwards to Formik's resetForm, which Formik wraps in useEventCallback and is itself stable, so the stale closure is safe; currentValues seeds the comparison once per loop start on purpose, so editing the form does not restart the loop
  }, [id, status]);
}
